import React, { useCallback, useEffect, useState } from 'react';

import { TableColumn } from '@silverfort/sf-components';

import { Incident } from '../../api/types/Incidents';
import IncidentColumn from './IncidentColumn';
import { INCIDENT_STATUS, INCIDENT_TYPE, LOG_TYPE } from '@silverfort-nodelibs/common/utils/consts';
import SeverityColumn from './SeverityColumn';
import StatusColumn from './StatusColumn';
import EntitiesColumn from './EntitiesColumn';
import BasicTableColumn from './BasicTableColumn';
import moment from 'moment/moment';

const APPROXIMATION_THRESHOLD = 2900;

interface IncidentColumnDefinition extends TableColumn<Incident> {
    extractValue: (row: Incident) => string | string[];
}

const getIncidentLabel = (kind: string) => {
    return Object.values(INCIDENT_TYPE).find((type) => type.apiName === kind)?.guiName ?? 'Not Found';
};

const getActivitiesText = (row: Incident) => {
    const numberText = row.auth_count > APPROXIMATION_THRESHOLD ? `~${row.auth_count}` : row.auth_count;

    const type = Object.values(INCIDENT_TYPE).find((type) => type.apiName === row.kind)?.incidentType;
    if (type === LOG_TYPE.EVENTS_LOG) {
        return `${numberText} ${row.auth_count === 1 ? 'event log' : 'event logs'}`;
    }
    return `${numberText} ${row.auth_count === 1 ? 'authentication' : 'authentications'}`;
};

const extractStatusValue = (rowStatus: string) => {
    return (
        Object.values(INCIDENT_STATUS).find((status) => status.label.toUpperCase() === rowStatus)?.label ?? 'Not Found'
    );
};

const useGetColumns = (updateIncidentStatus: (incidentIds: string[], status: number) => void) => {
    const [columns, setColumns] = useState<IncidentColumnDefinition[]>([]);

    const getIncidentsTableColumns = useCallback(() => {
        const relevantColumns: IncidentColumnDefinition[] = [
            {
                columnKey: 'kind',
                title: 'Incidents',
                render: (row) => {
                    return <IncidentColumn id={row.id} kind={row.kind} label={getIncidentLabel(row.kind)} />;
                },
                extractValue: (row) => row.id,
                columnClassName: 'min-w-56'
            },
            {
                columnKey: 'severity',
                title: 'Severity',
                render: (row) => <SeverityColumn severity={row.severity} />,
                extractValue: (row) => row.severity.toString(),
                columnClassName: 'min-w-20'
            },
            {
                columnKey: 'status',
                title: 'Status',
                render: (row) => (
                    <StatusColumn
                        status={row.status}
                        setStatus={(status) => {
                            updateIncidentStatus([row.id], status);
                        }}
                    />
                ),
                extractValue: (row) => extractStatusValue(row.status)
            },
            {
                columnKey: 'main_entities',
                title: 'Main entities',
                render: (row) => <EntitiesColumn mainEntities={row?.main_entities ?? []} />,
                extractValue: (row) => row?.main_entities?.map((entity) => entity.name ?? entity.identifier),
                columnClassName: 'min-w-56'
            },
            {
                columnKey: 'start_time',
                title: 'Start time',
                render: (row) => <BasicTableColumn text={moment(row.start_time).format('L LTS')} />,
                extractValue: (row) => moment(row.start_time).format('L LTS')
            },
            {
                columnKey: 'last_log_timestamp',
                title: 'Last activity',
                render: (row) => <BasicTableColumn text={moment(row.last_log_timestamp).format('L LTS')} />,
                extractValue: (row) => moment(row.last_log_timestamp).format('L LTS')
            },
            {
                columnKey: 'auth_count',
                title: '# Activities',
                render: (row) => <BasicTableColumn text={getActivitiesText(row)} />,
                extractValue: (row) => row?.auth_count.toString()
            }
        ];
        return relevantColumns;
    }, [updateIncidentStatus]);

    useEffect(() => {
        const relevantColumns = getIncidentsTableColumns();

        setColumns(relevantColumns);
    }, [getIncidentsTableColumns]);

    return columns;
};

export default useGetColumns;
